import { CSSProperties, useMemo } from 'react';
import styled from 'styled-components';

import { MenuItem, TextField } from '@material-ui/core';
import { useQuery } from '@tanstack/react-query';

import { adminListMarketsApi } from '../../api/markets/adminListMarketsApi';
import { Market } from '../../graphql/API';

type Props = {
  value?: string;
  handleOnChange: (value?: string) => void;
  containerStyle?: CSSProperties;
};

type MarketOption = {
  id: string;
  name: string;
  activeModels: number;
  activeVenues: number;
};

const ALL_MARKETS = 'all';

// "Los angeles" -> "Los Angeles": capital letter on every word, hyphenated ones included.
const titleCase = (name: string) =>
  name.toLowerCase().replace(/(^|[\s-])([a-zÀ-ſ])/g, (_, sep, ch) => sep + ch.toUpperCase());

export const MarketsSelect: React.FC<Props> = ({ value, handleOnChange, containerStyle }) => {
  const { data } = useQuery({
    queryKey: ['admin-list-markets-api'],
    queryFn: async () => {
      const data = await adminListMarketsApi({
        sortBy: 'name',
        sortDirection: 'asc',
        offset: 0,
        limit: 999,
      });
      return data;
    },
  });

  // Markets with the most active users first, so the empty ones sink to the bottom of the list.
  const items = useMemo<MarketOption[]>(() => {
    const options = (data || []).map((m: Market) => ({
      id: m.id,
      name: titleCase(m.name || ''),
      activeModels: m.totalActiveModels || 0,
      activeVenues: m.totalActiveVenues || 0,
    }));
    return options.sort(
      (a, b) =>
        b.activeModels - a.activeModels ||
        b.activeVenues - a.activeVenues ||
        a.name.localeCompare(b.name),
    );
  }, [data]);

  const selectedLabel = (selected: unknown) => {
    if (!selected || selected === ALL_MARKETS) return 'All Markets';
    return items.find((m) => m.id === selected)?.name || '';
  };

  return (
    <SelectContainer>
      <PortfolioBlock style={{ ...containerStyle }}>
        <TextField
          id={'Offer-Category'}
          fullWidth
          placeholder="Offer Category"
          variant="outlined"
          value={value}
          onChange={(e) => {
            handleOnChange(e.target.value);
          }}
          select
          SelectProps={{
            // The closed field shows only the market name, not the counters.
            renderValue: selectedLabel,
            MenuProps: {
              // Open below the field instead of over it.
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              getContentAnchorEl: null,
              // Cap the dropdown height so the long list of markets scrolls instead of filling the screen.
              PaperProps: { style: { maxHeight: 360, minWidth: 260, marginTop: 4 } },
            },
          }}
        >
          <MarketMenuItem value={ALL_MARKETS} divider>
            <MarketName>All Markets</MarketName>
          </MarketMenuItem>
          {items.map((m, index) => (
            <MarketMenuItem key={m.id} value={m.id} divider={index < items.length - 1}>
              <MarketName>{m.name}</MarketName>
              <MarketCounts>
                {m.activeModels} active users · {m.activeVenues} active businesses
              </MarketCounts>
            </MarketMenuItem>
          ))}
        </TextField>
      </PortfolioBlock>
    </SelectContainer>
  );
};

const SelectContainer = styled.div`
  width: 200px;
`;

const MarketMenuItem = styled(MenuItem)`
  && {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-top: 8px;
    padding-bottom: 8px;
    font-family: Poppins;
  }
`;

const MarketName = styled.span`
  font-family: Poppins;
  font-size: 14px;
  line-height: 20px;
`;

const MarketCounts = styled.span`
  font-family: Poppins;
  font-size: 12px;
  line-height: 16px;
  color: #757575;
`;

const PortfolioBlock = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  .MuiFormControl-root:focus-visible {
    outline: none !important;
  }
  .MuiSelect-root {
    padding: 7.5px 14px !important;
    font-size: 14px;
    font-family: Poppins;
  }
  .MuiSelect-select:focus-visible {
    outline: none !important;
  }
  .MuiOutlinedInput-root:focus-visible {
    outline: none !important;
  }
  .MuiTextField-root:focus-visible {
    outline: none !important;
  }
`;
